/* eslint-disable complexity */
import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import handleViewport from 'react-in-viewport'
import { ApolloClient, NormalizedCacheObject } from 'apollo-boost'
import * as Cookies from 'js-cookie'
import merge from 'lodash.merge'
import { GetServerSidePropsContext } from 'next'
import getConfig from 'next/config'
import dynamic from 'next/dynamic'
import Head from 'next/head'

import { ProductPageVariables } from '@thg-commerce/enterprise-aurora-product/resolvers/ProductPage/types'
import {
  generateObjectCacheKey,
  ObjectCacheKey,
  pageTTLConfig,
  PageType as CachePageType,
} from '@thg-commerce/enterprise-cache'
import { DeliveryAndReturnsInfo } from '@thg-commerce/enterprise-components'
import { HeaderZIndexLevel } from '@thg-commerce/enterprise-components/Header/types'
import { PersonalisationToggleContextProvider } from '@thg-commerce/enterprise-components/Personalisation'
import { ProductListRenderer } from '@thg-commerce/enterprise-components/ProductListRenderer/ProductListRenderer'
import { SponsoredAdsType } from '@thg-commerce/enterprise-components/SponsoredAds/queryBuilders'
import PowerReviewHead from '@thg-commerce/enterprise-components/src/PowerReview/PowerReviewHead'
import {
  CacheConfigurationProps,
  EnterpriseNextPageContext,
  ErrorBoundary,
  getSafeUrlParametersFromReq,
  i18n,
  PageWrapper,
  Routes,
  useEnterpriseContext,
  useHeaderHeight,
  useLoginCheck,
  useRequestConfig,
  useSiteConfig,
  useSiteDefinition,
  useTheme,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import {
  EnterpriseNextPage,
  PageType,
} from '@thg-commerce/enterprise-core/src/App/types'
import { InteractionLocation } from '@thg-commerce/enterprise-core/src/Basket'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { createGraphQLClient } from '@thg-commerce/enterprise-core/src/Factory/graphql'
import { loadSiteProperties } from '@thg-commerce/enterprise-core/src/i18n/siteProperties'
import { FullHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import { EnterpriseRequest } from '@thg-commerce/enterprise-core/src/Server/types'
import { addToDataLayer } from '@thg-commerce/enterprise-metrics'
import { RecommendationTrackingContext } from '@thg-commerce/enterprise-metrics/src/backend_event/types/tracking'
import {
  pushToDataLayer,
  pushToEnhancedDataLayer,
} from '@thg-commerce/enterprise-metrics/src/data_layer'
import {
  productEventsCategory,
  pushProductViewData,
} from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'
import { getHistogram } from '@thg-commerce/enterprise-metrics/src/prometheus/histogram'
import { Resolvers } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers'
import {
  ComponentName,
  ComponentWidgetsData,
  ComponentWidgetsVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/ComponentWidgets'
import {
  ManualRecommendationOptions,
  ProductPageData,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import { ProductVariantWithDescription } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Product'
import {
  Country,
  Currency,
  Feature,
  FulfilmentMethod,
  Maybe,
  ProductRecommendationType,
  QueryPageArgs,
  ReviewSort,
  Widget,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  ChoiceMap,
  VariantChoices,
} from '@thg-commerce/enterprise-network/src/transformers/product/options'
import {
  loadPageTheme,
  PageTheme,
  ProductPageLayout,
} from '@thg-commerce/enterprise-pages'
import { PromotionalMessagePriority } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { ProductOptionsLink } from '@thg-commerce/enterprise-product-options/src/components/ProductOptionsLink'
import { getSelectedOptions } from '@thg-commerce/enterprise-product-options/src/utils/options'
import {
  mq,
  spacing,
  styled,
  ThemeProvider,
} from '@thg-commerce/enterprise-theme'
import {
  calculateDiscount,
  sanitizeCookie,
} from '@thg-commerce/enterprise-utils'
import { LoyaltyStatusBar } from '@thg-commerce/enterprise-widget-loyalty-status-bar'
import { WidgetRendererProps } from '@thg-commerce/enterprise-widgets/src/WidgetRenderer/WidgetRenderer'
import { PlatformMessage, Separator } from '@thg-commerce/gravity-elements'
import { Grid, GridItem } from '@thg-commerce/gravity-system'
import { zIndex } from '@thg-commerce/gravity-theme'

import {
  BuyNowPayLaterContainer,
  FreeDeliveryIndicator,
  HullabalookComponent,
  ProductAlternatives,
  ProductInfoBoxes,
  ProductSummary,
  PromotionalMessage,
  RecentlyViewedProductRail,
} from '../../src/components'
import {
  PrefetchTagProps,
  PrefetchTags,
} from '../../src/components/PrefetchTags/PrefetchTags'
import { ProductOptions } from '../../src/components/product/ProductOptions'
import { ProductOptionKeys } from '../../src/components/ProductReviews/PaginatedProductReviews/OtherCustomersBought/OtherCustomersBought'
import {
  REVIEW_CARD_HEIGHT,
  REVIEWS_INITIALLY_RENDERED,
  TopProductReviewsProps,
} from '../../src/components/ProductReviews/TopProductReviews/TopProductReviews'
import { getReviewFiltersFromUrl } from '../../src/components/ProductReviews/utils'
import { ProductSchema } from '../../src/components/ProductSchema'
import { ProductStockInformation } from '../../src/components/ProductStockInformation'
import { ProductTitleAndLogo } from '../../src/components/ProductTitleAndLogo'
import { SizeGuideContainerProps } from '../../src/components/SizeGuideModal/SizeGuideContainer/SizeGuideContainer'
import { ComponentWidgets as WIDGET_QUERY } from '../../src/ComponentWidgets.graphql'
import { ConcessionQuery as PRODUCT_CONCESSION_QUERY } from '../../src/ConcessionQuery.graphql'
import { ProductPageContextProvider } from '../../src/context/ProductPageContext'
import { GlobalComponentWidgets as GLOBAL_COMPONENT_QUERY_AURORA } from '../../src/graphql/GlobalComponentWidgets.graphql'
import { ProductPage as PRODUCT_PAGE_QUERY_AURORA } from '../../src/graphql/ProductPage.graphql'
import {
  FrequentlyBoughtTogetherRenderer,
  MarketedSpecialOfferSummaryRenderer,
  OutOfStockNotificationRenderer,
  ProductBreadcrumbsRenderer,
  ProductDescriptionAccordionRenderer,
  ProductImageGalleryRenderer,
  ProductRecommendationsRenderer,
} from '../../src/renderers'
import CustomError from '../_error'

const LiveChat = dynamic<{}>(() =>
  import('@thg-commerce/enterprise-components/src/LiveChat').then(
    (mod) => mod.LiveChat,
  ),
)

const ADAChat = dynamic<{}>(() =>
  import('@thg-commerce/enterprise-components/src/ADAChat').then(
    (mod) => mod.ADAChat,
  ),
)

const SizeGuideContainer = dynamic<SizeGuideContainerProps>(() =>
  import('../../src/components/SizeGuideModal/SizeGuideContainer').then(
    (mod) => mod.SizeGuideContainer,
  ),
)
const TopProductReviews = dynamic<TopProductReviewsProps>(() =>
  import('../../src/components/ProductReviews/TopProductReviews').then(
    (mod) => mod.TopProductReviews,
  ),
)

const StoryStreamRendererComponent = dynamic<{}>(() =>
  import('../../src/renderers').then((mod) => mod.StoryStreamRenderer),
)

const PowerReviewDisplayRenderer = dynamic<{}>(() =>
  import('../../src/components/PowerReviewDisplay/PowerReviewDisplay').then(
    (mod) => mod.PowerReviewDisplay,
  ),
)

const RecommendationItemListRenderer = dynamic<{}>(() =>
  import('../../src/renderers/RecommendationItemListRenderer').then(
    (mod) => mod.RecommendationItemListRenderer,
  ),
)

const ProductStoreStockRenderer = dynamic<{}>(() =>
  import('../../src/components/ProductStoreStock').then(
    (mod) => mod.ProductStoreStock,
  ),
)

const WidgetRenderer = dynamic<WidgetRendererProps>(() =>
  import('@thg-commerce/enterprise-widgets/src/WidgetRenderer').then(
    (mod) => mod.WidgetRenderer,
  ),
)

const FrequentlyBoughtTogether = withHydrationOnDemand({
  on: [['scroll', () => document]],
})(FrequentlyBoughtTogetherRenderer)

const StoryStreamRenderer = withHydrationOnDemand({
  on: [['idle']],
})(StoryStreamRendererComponent)

const ProductStoreStockRendererComponent = withHydrationOnDemand({
  on: [['idle']],
})(ProductStoreStockRenderer)

const ProductRecommendationsRendererComponent = withHydrationOnDemand({
  on: [['idle']],
})(ProductRecommendationsRenderer)

const ProductImageGalleryRendererComponent = withHydrationOnDemand({
  on: [['idle']],
})(ProductImageGalleryRenderer)

const TopProductReviewsComponent = withHydrationOnDemand({
  on: [['visible']],
})(TopProductReviews)

const PowerReviewDisplayRendererComponent = withHydrationOnDemand({
  on: [['delay', 500]],
})(PowerReviewDisplayRenderer)

const WidgetRendererComponent = withHydrationOnDemand({
  on: [['delay', 500]],
})(WidgetRenderer)

const StyledGrid = styled(Grid)`
  margin: 0 auto;
  padding: ${spacing(0)} ${spacing(2)} ${spacing(2)};
  max-width: ${(props) => props.theme.site.siteWidth};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: ${spacing(0)} ${spacing(4)} ${spacing(4)};
  }
`
const StyledAlternateGrid = styled(Grid)<{
  gridGap?: string
}>`
  margin: 0 auto;
  ${({ gridGap }) => gridGap && `grid-gap: ${gridGap};`}
  max-width: ${(props) => props.theme.site.siteWidth};
`

const SimpleGrid = styled(Grid)`
  margin: 0;
`

const SimpleGridItem = styled(GridItem)<{
  stickyTopPosition?: number
  margin?: string
}>`
  ${(props) => props.margin && `margin: ${props.margin};`}

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    ${(props) =>
      props.stickyTopPosition &&
      `position: sticky;
       height: fit-content;
       top: ${props.stickyTopPosition}px;`}
  }
`

const OptionsWrapper = styled.div<{ headerHeight: number }>`
  ${zIndex(HeaderZIndexLevel.Tooltip)};
  top: ${(props) => `${props.headerHeight}px`};
  display: flex;
  flex-direction: column;
`

const StyledPlatformMessage = styled(PlatformMessage)`
  margin-top: ${spacing(4)};
`

const StyledSeparator = styled(Separator)`
  width: 100%;
`

export enum PlatformActionType {
  noAction = '',
  reviewSuccessfulSubmission = 'successfulsubmission',
}

const HideOnMobile = styled.div`
  display: none;
  visibility: hidden;
  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    display: initial;
    visibility: initial;
  }
`

const HideOnDesktop = styled.div`
  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    display: none;
    visibility: hidden;
  }
`

const StyledMarketedSpecialOfferSummaryRenderer = styled(
  MarketedSpecialOfferSummaryRenderer,
)`
  margin-bottom: ${spacing(5)};
`

// Set to 9 because one of the skus will always be the one currently displayed on pdp
// which is ignored when showing recently viewed products
const MAX_RECENT_SKUS = 9
const RECENT_PRODUCTS_COOKIE_NAME = 'recentlyViewedProducts'

const updateRecentlyViewedProductsCookie = (
  sku: number,
  storedSkusLimit: number,
) => {
  const cookieValue = sanitizeCookie(RECENT_PRODUCTS_COOKIE_NAME)
  const cookieData: number[] = cookieValue
    ? cookieValue.split(',').map((skuEntry) => parseInt(skuEntry, 10))
    : []

  if (!cookieData) Cookies.set(RECENT_PRODUCTS_COOKIE_NAME, sku)
  else {
    if (cookieData.includes(sku)) cookieData.splice(cookieData.indexOf(sku), 1)

    cookieData.unshift(sku)

    if (cookieData.length > storedSkusLimit) {
      cookieData.pop()
    }
    Cookies.set(RECENT_PRODUCTS_COOKIE_NAME, cookieData.join(','))
  }
}

const processedVariantExternalId = (
  useExternalIdentifier: boolean,
  product: ProductPageData,
) =>
  useExternalIdentifier && product.defaultVariant?.externalIdentifier
    ? product.defaultVariant.externalIdentifier
    : product.defaultVariant?.sku.toString() || ''

const handleProductViewEvent = (
  product,
  enhancedEcommerceEnabled,
  useExternalIdentifier,
  itemCategoryString,
  rootCategories,
) => {
  if (!enhancedEcommerceEnabled) {
    return
  }

  const homeDeliveryAvailable = product?.defaultVariant
    ?.eligibleForFulfilmentMethods
    ? Boolean(
        product.defaultVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.HomeDelivery,
        ),
      )
    : true
  const clickAndCollectAvailable = product.defaultVariant
    ?.eligibleForFulfilmentMethods
    ? Boolean(
        product.defaultVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.CollectInStore,
        ),
      )
    : false

  pushToEnhancedDataLayer<ProductDetailEnhancedEcommerceEvent>({
    event: 'productView',
    ecommerce: {
      detail: {
        products: [
          {
            ...pushProductViewData(
              product.title,
              useExternalIdentifier && product.externalIdentifier
                ? product.externalIdentifier
                : product.sku.toString(),
              product.defaultVariant?.price?.price.amount,
              product.brand?.name,
              product.inStock,
              homeDeliveryAvailable,
              clickAndCollectAvailable,
              product.isCheckStock,
              !product.isCheckStock,
              (product.defaultVariant.eligibleForFulfilmentMethods?.[0] &&
                product.defaultVariant.eligibleForFulfilmentMethods?.[0].toLowerCase()) ||
                'out',
              itemCategoryString,
              product.priceRange,
              processedVariantExternalId(useExternalIdentifier, product),
              rootCategories,
              product.inStockSizes,
            ),
          },
        ],
      },
    },
  })
}

const isAlternateProductKeys = (
  alternateProductKeysForCategories: string[] = [],
) => {
  return (
    alternateProductKeysForCategories &&
    alternateProductKeysForCategories?.length > 0
  )
}

const manualRecsType = (manualRecommendationType, manualRecsConfig) => {
  return typeof manualRecommendationType === 'string'
    ? (manualRecommendationType as ProductRecommendationType)
    : manualRecsConfig
}

const getItemId = (
  product,
  useExternalIdentifier,
  useExternalIdentifierInSchema,
) => {
  if (
    (useExternalIdentifier || useExternalIdentifierInSchema) &&
    product.externalIdentifier
  ) {
    return product.externalIdentifier
  }
  return product.sku?.toString() || ''
}

const buildItemData = (
  product,
  useExternalIdentifier,
  useExternalIdentifierInSchema,
  alternateProductKeysForCategories,
  currency,
) => ({
  item_name: product?.defaultVariant.title || '',
  item_id: getItemId(
    product,
    useExternalIdentifier,
    useExternalIdentifierInSchema,
  ),
  price: product?.defaultVariant?.price?.price.amount || '',
  item_brand: product?.brand?.name || '',
  item_variant:
    (useExternalIdentifier || useExternalIdentifierInSchema) &&
    product.defaultVariant?.externalIdentifier
      ? product.defaultVariant?.externalIdentifier
      : product.defaultVariant?.sku?.toString(),
  discount: calculateDiscount(
    product?.defaultVariant?.price?.rrp.amount,
    product?.defaultVariant?.price?.price.amount,
  ),
  item_external_id: product.externalIdentifier || product.sku?.toString(),
  item_currency: currency,
  item_page: InteractionLocation.PDP,
  item_list_name: product.title,
  quantity: 1,
  ...productEventsCategory(
    product.categories,
    alternateProductKeysForCategories,
  ),
  index: 0,
})

const eventDataLayer = (
  useGA4EnhancedEcom,
  product,
  currency,
  useExternalIdentifier,
  useExternalIdentifierInSchema,
  alternateProductKeysForCategories,
) => {
  if (useGA4EnhancedEcom) {
    const itemData = buildItemData(
      product,
      useExternalIdentifier,
      useExternalIdentifierInSchema,
      alternateProductKeysForCategories,
      currency,
    )

    pushToEnhancedDataLayer({
      event: 'ecom_event',
      event_name: 'view_item',
      ecommerce: {
        currencyCode: currency,
        items: [itemData],
      },
    })
  }
}

const getProductHasManualRecommendations = (
  manualRecommendationsEnabled,
  manualRecommendations,
) => {
  return (
    manualRecommendationsEnabled && (manualRecommendations || []).length > 0
  )
}

const productDetailsGA4 = (product, hullabalookRecommendationsEnabled) => {
  const inStockVariant =
    product.inStock || product.variants.some((variant) => variant.inStock)
  const productData = [
    {
      productPrice: product?.defaultVariant?.price?.price.amount,
      productRating: product?.reviews?.averageScore?.toString() || '',
      productReviews: product?.reviews?.reviews?.total?.toString() || '',
      productSKU: product?.sku?.toString(),
      productStatus: inStockVariant ? 'Available' : 'Unavailable',
      // productType: product.type?.productType || '',
    },
    {
      hullabalookRecommendationsEnabled,
      platform: 'Elysium2',
      productBrand: product?.brand?.name || '',
      productImages: product?.images?.length?.toString() || '',
      productStarRating: product?.reviews?.averageScore?.toString() || '',
      // productCategory: product.brand?.page?.path || '',
      // productVideoReference: product,
    },
  ]

  const pageTitle = product.title.concat(' Product Page')

  addToDataLayer({ key: 'productDetails', value: productData })
  addToDataLayer({ key: 'pageTitle', value: pageTitle })
  pushToDataLayer({
    type: 'elysiumEvent',
    eventData: {
      eventAction: 'Viewed',
      eventCategory: 'Product | Description',
      eventLabel: 'productDescription component',
      // eventLabelValue: undefined,
      eventPage: product.url,
    },
  })
}

interface ProductPageProps {
  preselectVariant?: boolean
  pageTheme?: PageTheme<ProductPageLayout> | null
  product?: ProductPageData
  platformActionType?: PlatformActionType
  prefetchLinks: PrefetchTagProps
  manualRecommendations: ManualRecommendationOptions[] | null
  componentWidgets: ComponentWidgetsData['componentWidgets']
  variationSkuProduct?: ProductVariantWithDescription
  reviewsSort?: ReviewSort
  reviewFilters?: string
  notFoundPageWidgets?: Widget[]
  enableHullabalookRecommendations?: boolean
}

interface ProductDetailEnhancedEcommerceEvent {
  event: 'productView'
  ecommerce: {
    detail: {
      products: {
        ['Home Delivery']?: string
        ['In-Store']?: string
        ['Not available for delivery']?: string
        ['Home Deliverable']?: string
        brand?: string
        category: string
        dimension45?: string
        dimension46?: string
        dimension48?: string
        item_id: string
        item_variant: string
        name: string
        price: string
        range: string
      }[]
    }
  }
}

const processedProductKeys = (
  alternateProductKeysForCategories: string[],
  useGA4EnhancedEcom: boolean,
) => {
  const alternateProductKeysEnabled =
    alternateProductKeysForCategories &&
    alternateProductKeysForCategories.length > 0

  return alternateProductKeysEnabled
    ? alternateProductKeysForCategories
    : useGA4EnhancedEcom
    ? ['isProvenanceEnabled', 'productMessage', 'productCategory']
    : ['isProvenanceEnabled', 'productMessage']
}

const getCurrency = (currency, defaultCurrency) =>
  currency || (defaultCurrency as Currency)

const getShippingDestination = (shippingDestination, code) =>
  shippingDestination || (code as Country)

const ProductPage: EnterpriseNextPage<ProductPageProps> = (props) => {
  const theme = useTheme()
  const headerHeight = useHeaderHeight()
  const {
    enhancedEcommerceEnabled,
    useExternalIdentifier,
    useExternalIdentifierInSchema,
    qubit,
    productPageOptionKeysWithAttribute,
    papMessageTopPosition,
    showPapMessageAboveBNPL,
    showOnePAPmessageOnPDP,
    enableProductDescriptionTab,
    disableDeliveryInformationInfo,
    useGA4EnhancedEcom,
    enablePDPStickyScroll,
    showTopProductBrandLogoAndTitleOnMobile,
    showPdpReviewsAboveRecommendations,
    hideRecentlyViewedProductsRailPDP,
    displayPriceAboveQuantity,
    enableProductRecommendationGrid,
    moveProductDescriptionAccordionToLHS,
    showPowerReview,
    showPapMessageInProductSummary,
    enableProductPersonalisationInterface,
    alternateProductKeysForCategories,
    hasAdaChatbot,
    hideADAChatOnPDP,
    showFrequentlyBoughtBelowAccordion,
    hidePDPBuyingComponents,
    enablePDPStoreStock,
    enableLoyaltyPointsStatusBar,
    enableManualRecommendationsBuild,
    useProductFreeDeliveryAlt,
  } = useSiteConfig()
  const isLoggedIn = useLoginCheck()
  const { originUrl, siteCode } = useSiteDefinition()
  const { horizonFeatures, currency, extensionsRef } = useEnterpriseContext()
  const { isMobileApp } = useRequestConfig()
  const subscriptionsEnabled = horizonFeatures?.includes(Feature.Subscriptions)
  const manualRecommendationsEnabled =
    !enableManualRecommendationsBuild &&
    horizonFeatures?.includes(Feature.ManualRecommendations)

  const productHasManualRecommendations = getProductHasManualRecommendations(
    manualRecommendationsEnabled,
    props.product?.manualRecommendations,
  )

  const i18nText = {
    siteTitleEnd: i18n('titles.end.text'),
    metaTagKeywordsEnd: i18n('meta.keywords.end.text'),
    addReviewsSuccessfulSubmission: i18n(
      'product.review.type.successfulsubmission',
    ),
    metaDescrPart1: i18n('meta.description.product.1.text'),
    metaDescrPart2: i18n('meta.description.product.2.text'),
    metaDescrPart3: i18n('meta.description.product.3.text'),
    subscriptionTitle: i18n('product.subscription.information.title'),
    subscriptionListItem1: i18n('product.subscription.information.list.text1'),
    subscriptionListItem2: i18n('product.subscription.information.list.text2'),
    subscriptionListItem3: i18n('product.subscription.information.list.text3'),
  }

  React.useEffect(() => {
    if (props.product) {
      productDetailsGA4(props.product, props.enableHullabalookRecommendations)

      handleProductViewEvent(
        props.product,
        enhancedEcommerceEnabled,
        useExternalIdentifier || useExternalIdentifierInSchema,
        props.product?.category,
        productEventsCategory(
          props.product.categories,
          alternateProductKeysForCategories,
        )?.root_category,
      )
      eventDataLayer(
        useGA4EnhancedEcom,
        props.product,
        currency,
        useExternalIdentifier,
        useExternalIdentifierInSchema,
        alternateProductKeysForCategories,
      )
    }
  }, [
    props.product,
    currency,
    enhancedEcommerceEnabled,
    useExternalIdentifier,
    useExternalIdentifierInSchema,
    useGA4EnhancedEcom,
    alternateProductKeysForCategories,
    props.enableHullabalookRecommendations,
  ])

  React.useEffect(() => {
    if (
      typeof window !== undefined &&
      window.uv &&
      qubit?.enabled &&
      props.product?.qubitProduct
    ) {
      window.uv?.emit('ecProduct', {
        eventType: 'detail',
        product: {
          ...props.product.qubitProduct,
          sku:
            props.variationSkuProduct?.sku?.toString() ??
            props.product.qubitProduct.sku,
        },
      })
    }
  }, [
    props.product?.qubitProduct,
    props.variationSkuProduct?.sku,
    qubit?.enabled,
  ])

  enum MarketedSpecialOfferSlot {
    One = 'MarketedSpecialOfferSlot1',
    Two = 'MarketedSpecialOfferSlot2',
    Three = 'MarketedSpecialOfferSlot3',
    Four = 'MarketedSpecialOfferSlot4',
  }

  enum PromotionalMessageSlot {
    One = 'PromotionalMessageSlot1',
    Two = 'PromotionalMessageSlot2',
  }

  const MarketedSpecialOfferSlotMap = React.useCallback(
    (matchedSlot: string) => {
      const slotEnableConditions = {
        MarketedSpecialOfferSlot1: {
          enabled:
            papMessageTopPosition &&
            !showOnePAPmessageOnPDP &&
            !productHasManualRecommendations &&
            !showPapMessageAboveBNPL &&
            !showPapMessageInProductSummary,
          props: {},
        },
        MarketedSpecialOfferSlot2: {
          enabled: showPapMessageAboveBNPL && !showPapMessageInProductSummary,
          props: {},
        },
        MarketedSpecialOfferSlot3: {
          enabled:
            !papMessageTopPosition &&
            !showPapMessageAboveBNPL &&
            !showPapMessageInProductSummary,
          props: {},
        },
        MarketedSpecialOfferSlot4: {
          enabled: !hidePDPBuyingComponents && showPapMessageInProductSummary,
          props: {
            topPositionPdp: true,
          },
        },
      }

      return slotEnableConditions[matchedSlot]?.enabled ? (
        <MarketedSpecialOfferSummaryRenderer
          {...(slotEnableConditions[matchedSlot]?.props || {})}
        />
      ) : null
    },
    [
      papMessageTopPosition,
      showOnePAPmessageOnPDP,
      productHasManualRecommendations,
      showPapMessageAboveBNPL,
      showPapMessageInProductSummary,
      hidePDPBuyingComponents,
    ],
  )

  const { pageTheme } = props

  const PromotionalMessageSlotMap = React.useCallback(
    (matchedSlot: string) => {
      const slotEnableConditions = {
        PromotionalMessageSlot1: {
          enabled:
            pageTheme?.theme.promotionalMessage?.priority ===
            PromotionalMessagePriority.HIGH,
        },
        PromotionalMessageSlot2: {
          enabled:
            !hidePDPBuyingComponents &&
            pageTheme?.theme.promotionalMessage?.priority !==
              PromotionalMessagePriority.HIGH,
        },
      }

      return slotEnableConditions[matchedSlot]?.enabled ? (
        <PromotionalMessage
          warningKeywords={pageTheme?.theme.promotionalMessage?.warningKeywords}
        />
      ) : null
    },
    [pageTheme, hidePDPBuyingComponents],
  )

  if (!props.product) {
    return (
      <CustomError
        statusCode={404}
        componentWidgets={props.notFoundPageWidgets}
      />
    )
  }

  updateRecentlyViewedProductsCookie(props.product.sku, MAX_RECENT_SKUS)

  const RecentlyViewedProductsViewport = handleViewport(
    RecentlyViewedProductRail,
    {
      // render when first review enters the viewport
      rootMargin: `${REVIEWS_INITIALLY_RENDERED * REVIEW_CARD_HEIGHT}px`,
    },
  )
  const FrequentlyBoughtTogetherContainer = showFrequentlyBoughtBelowAccordion
    ? HideOnDesktop
    : React.Fragment
  const {
    value: [getExtensions],
  } = extensionsRef
  const extensions = getExtensions()

  const showProductDescriptionOnLHS =
    moveProductDescriptionAccordionToLHS || productHasManualRecommendations
  return (
    <React.Fragment>
      <Head>
        <title>{`${props.product.title} ${i18nText.siteTitleEnd}`}</title>
        <meta
          name="description"
          content={
            props.product.metaDescription
              ? props.product.metaDescription
              : `${i18nText.metaDescrPart1} ${props.product.title} ${i18nText.metaDescrPart2} ${i18nText.metaDescrPart3}`.trim()
          }
        />
        {props.product.alternateLinks.map((alternateLink) => {
          return (
            <link
              rel="alternate"
              href={alternateLink.url}
              hrefLang={alternateLink.hreflang}
            />
          )
        })}
        <ProductOptionsLink originUrl={originUrl} sku={props.product.sku} />
      </Head>
      <PowerReviewHead />
      <PageWrapper compactMargin={true} sendPageVisit={true}>
        <GridItem colSpan={12}>
          <ThemeProvider
            theme={merge(theme, { pageTheme: props.pageTheme?.theme })}
          >
            <PersonalisationToggleContextProvider
              enableProductPersonalisationInterface={
                enableProductPersonalisationInterface
              }
            >
              <ProductPageContextProvider
                product={props.product}
                manualRecommendations={props.manualRecommendations}
                variationSkuProduct={props.variationSkuProduct}
                preselectVariant={props.preselectVariant ?? true}
                enableHullabalookRecommendations={
                  props.enableHullabalookRecommendations
                }
              >
                {!isMobileApp && <ProductBreadcrumbsRenderer />}
                {showTopProductBrandLogoAndTitleOnMobile && (
                  <ProductTitleAndLogo
                    textStyle={
                      props.pageTheme?.theme.productSummary.title?.textStyle
                    }
                  />
                )}
                <WidgetRendererComponent widgets={props.componentWidgets} />
                <PrefetchTags
                  defaults={props.prefetchLinks.defaults}
                  variants={props.prefetchLinks.variants}
                />
                {props.product.allVariantsOutOfStock && <ProductAlternatives />}
                <StyledGrid>
                  <GridItem colStart={1} colSpan={12} rowStart={1}>
                    {props.platformActionType &&
                      props.platformActionType ===
                        PlatformActionType.reviewSuccessfulSubmission && (
                        <StyledPlatformMessage
                          type="success"
                          text={i18nText.addReviewsSuccessfulSubmission}
                          data-testid={`${PlatformActionType.reviewSuccessfulSubmission}-platform-message`}
                        />
                      )}
                  </GridItem>
                  <SimpleGridItem
                    colStart={1}
                    colSpan={[12, 12, 7, 7]}
                    rowStart={2}
                    rowSpan={[1, 1, 4, 4]}
                    margin="0"
                    {...(enablePDPStickyScroll && {
                      stickyTopPosition: headerHeight,
                    })}
                  >
                    <ProductImageGalleryRendererComponent />
                    {enableProductDescriptionTab &&
                    showProductDescriptionOnLHS ? (
                      <HideOnMobile>
                        <SimpleGrid columns={7}>
                          <SimpleGridItem
                            colStart={
                              props.pageTheme?.theme.productDescription
                                .accordion.colStart ?? 1
                            }
                            colSpan={
                              props.pageTheme?.theme.productDescription
                                .accordion.colSpan ?? 7
                            }
                            rowStart={1}
                            margin="0"
                          >
                            <ProductDescriptionAccordionRenderer
                              isDesktop={true}
                              theme={props.pageTheme?.theme.productDescription}
                            />
                          </SimpleGridItem>
                        </SimpleGrid>
                      </HideOnMobile>
                    ) : null}
                    {showFrequentlyBoughtBelowAccordion &&
                      !hidePDPBuyingComponents && (
                        <HideOnMobile>
                          <FrequentlyBoughtTogether />
                        </HideOnMobile>
                      )}
                  </SimpleGridItem>
                  <SimpleGridItem
                    colStart={[1, 1, 8, 8]}
                    colSpan={[12, 12, 5, 5]}
                    rowStart={[3, 3, 2, 2]}
                    {...(enablePDPStickyScroll && {
                      stickyTopPosition: headerHeight,
                    })}
                  >
                    <GridItem
                      colStart={[1, 1, 1, 1]}
                      colSpan={[12, 12, 12, 12]}
                      rowStart={[2, 2, 2, 2]}
                    >
                      {!productHasManualRecommendations ? (
                        <ProductSummary>
                          <>
                            {MarketedSpecialOfferSlotMap(
                              MarketedSpecialOfferSlot.Four,
                            )}
                            {PromotionalMessageSlotMap(
                              PromotionalMessageSlot.One,
                            )}
                          </>
                        </ProductSummary>
                      ) : (
                        <React.Fragment>
                          <RecommendationItemListRenderer />
                          {enablePDPStoreStock && (
                            <ProductStoreStockRendererComponent />
                          )}
                          <ProductInfoBoxes headerHeight={headerHeight} />
                          {!hideADAChatOnPDP && hasAdaChatbot ? (
                            <ADAChat />
                          ) : (
                            <LiveChat />
                          )}
                          {enableLoyaltyPointsStatusBar && isLoggedIn && (
                            <LoyaltyStatusBar
                              originUrl={originUrl}
                              loyaltyEnabled={horizonFeatures?.includes(
                                Feature.Loyalty,
                              )}
                              currency={currency}
                            />
                          )}
                        </React.Fragment>
                      )}
                      {!hidePDPBuyingComponents &&
                        MarketedSpecialOfferSlotMap(
                          MarketedSpecialOfferSlot.One,
                        )}
                    </GridItem>
                    <GridItem colStart={1} colSpan={12} rowStart={[1, 1, 7, 7]}>
                      {!productHasManualRecommendations && (
                        <OptionsWrapper headerHeight={headerHeight}>
                          {!productPageOptionKeysWithAttribute?.includes(
                            ProductOptionKeys.SIZE,
                          ) && (
                            <SizeGuideContainer
                              sizeGuide={props.product.sizeGuide}
                              hasMarginBottom
                              modalLinkTextStyling={
                                props.pageTheme?.theme.sizeGuide.modalLink
                                  ?.textStyle
                              }
                            />
                          )}
                          {!displayPriceAboveQuantity &&
                          !useProductFreeDeliveryAlt ? (
                            <FreeDeliveryIndicator />
                          ) : (
                            !subscriptionsEnabled &&
                            !props.pageTheme?.theme.sizeGuide
                              .hideSizeGuideSeparator && (
                              <Separator withSpacing />
                            )
                          )}
                          {!hidePDPBuyingComponents && <ProductOptions />}
                          <ProductStockInformation
                            variants={props.product.variants}
                          />
                          {useProductFreeDeliveryAlt && (
                            <FreeDeliveryIndicator />
                          )}
                          {!hidePDPBuyingComponents &&
                            MarketedSpecialOfferSlotMap(
                              MarketedSpecialOfferSlot.Two,
                            )}
                          {!hidePDPBuyingComponents && (
                            <BuyNowPayLaterContainer />
                          )}
                          <OutOfStockNotificationRenderer mainProduct={true} />
                          {enablePDPStoreStock && (
                            <ProductStoreStockRendererComponent />
                          )}
                          <ProductInfoBoxes headerHeight={headerHeight} />

                          {!hidePDPBuyingComponents &&
                            MarketedSpecialOfferSlotMap(
                              MarketedSpecialOfferSlot.Three,
                            )}
                          {PromotionalMessageSlotMap(
                            PromotionalMessageSlot.Two,
                          )}
                          {!disableDeliveryInformationInfo &&
                            showOnePAPmessageOnPDP &&
                            papMessageTopPosition && (
                              <StyledSeparator withSpacing />
                            )}
                          {!disableDeliveryInformationInfo &&
                            !hidePDPBuyingComponents && (
                              <DeliveryAndReturnsInfo
                                deliveryMessageTextStyle={
                                  props.pageTheme?.theme.deliveryAndReturnsInfo
                                    .fontWeight
                                }
                                returnsTitleStyle={
                                  props.pageTheme?.theme.deliveryAndReturnsInfo
                                    .returnsTitleStyle
                                }
                              />
                            )}
                          <ProductListRenderer
                            sponsoredAdsType={SponsoredAdsType.PDP}
                            skuWithSponsoredAds={props.product.sku}
                          />
                          {!hideADAChatOnPDP && hasAdaChatbot ? (
                            <ADAChat />
                          ) : (
                            <LiveChat />
                          )}
                          {enableLoyaltyPointsStatusBar && isLoggedIn && (
                            <LoyaltyStatusBar
                              originUrl={originUrl}
                              loyaltyEnabled={horizonFeatures?.includes(
                                Feature.Loyalty,
                              )}
                              currency={currency}
                            />
                          )}
                        </OptionsWrapper>
                      )}
                      {enableProductDescriptionTab &&
                      !showProductDescriptionOnLHS ? (
                        <HideOnMobile>
                          <ProductDescriptionAccordionRenderer
                            isDesktop={true}
                            theme={props.pageTheme?.theme.productDescription}
                          />
                        </HideOnMobile>
                      ) : null}
                      {productHasManualRecommendations &&
                        papMessageTopPosition &&
                        !showPapMessageAboveBNPL &&
                        !hidePDPBuyingComponents && (
                          <div
                            style={{
                              marginTop: `${
                                productHasManualRecommendations
                                  ? `-${spacing(1)}`
                                  : spacing(2)
                              }`,
                            }}
                          >
                            <StyledMarketedSpecialOfferSummaryRenderer />
                          </div>
                        )}
                    </GridItem>
                  </SimpleGridItem>
                </StyledGrid>
                <StyledGrid>
                  <GridItem
                    colStart={1}
                    colSpan={12}
                    rowStart={2}
                    style={{ maxWidth: '100%' }}
                    data-testid="product-reviews-and-recommendations"
                  >
                    <HideOnDesktop>
                      <ProductDescriptionAccordionRenderer
                        theme={props.pageTheme?.theme.productDescription}
                      />
                    </HideOnDesktop>
                    {enableManualRecommendationsBuild && (
                      <ProductRecommendationsRendererComponent
                        product={props.product}
                        displayRecommendationsBuild={
                          enableManualRecommendationsBuild
                        }
                      />
                    )}
                    {!hidePDPBuyingComponents && (
                      <FrequentlyBoughtTogetherContainer>
                        <FrequentlyBoughtTogether />
                      </FrequentlyBoughtTogetherContainer>
                    )}
                    <StoryStreamRenderer />
                    {showPdpReviewsAboveRecommendations && (
                      <TopProductReviewsComponent
                        product={props.product}
                        reviewFilters={props.reviewFilters}
                        enableAlternativeText={
                          extensions?.experiments
                            ?.ely_2_review_title_wave_01 ===
                          'v1|new_text_format'
                        }
                      />
                    )}
                    {props.enableHullabalookRecommendations && (
                      <HullabalookComponent
                        siteCode={siteCode}
                        experienceLabel="nvb-womenswear"
                      />
                    )}
                    <ProductRecommendationsRendererComponent
                      product={props.product}
                      trackingContext={
                        RecommendationTrackingContext.PERSONALISED
                      }
                      displayAsGrid={enableProductRecommendationGrid}
                    />
                    {!showPdpReviewsAboveRecommendations &&
                      (showPowerReview ? (
                        <PowerReviewDisplayRendererComponent />
                      ) : (
                        <TopProductReviewsComponent
                          product={props.product}
                          reviewFilters={props.reviewFilters}
                          enableAlternativeText={
                            extensions?.experiments
                              ?.ely_2_review_title_wave_01 ===
                            'v1|new_text_format'
                          }
                        />
                      ))}
                    <ProductSchema />
                  </GridItem>
                </StyledGrid>
                <StyledAlternateGrid
                  gridGap={
                    props.pageTheme?.theme?.recentlyViewedProducts?.container
                      ?.gridGap
                  }
                >
                  {!hideRecentlyViewedProductsRailPDP && (
                    <GridItem
                      colSpan={12}
                      rowStart={12}
                      data-testid="recently-viewed-products"
                    >
                      <ErrorBoundary>
                        <RecentlyViewedProductsViewport />
                      </ErrorBoundary>
                    </GridItem>
                  )}
                </StyledAlternateGrid>
              </ProductPageContextProvider>
            </PersonalisationToggleContextProvider>
          </ThemeProvider>
        </GridItem>
      </PageWrapper>
    </React.Fragment>
  )
}

ProductPage.Layout = FullHeaderLayout

ProductPage.pageType = PageType.PRODUCT

ProductPage.getConcessionCode = async (
  ctx: EnterpriseNextPageContext,
  apolloClient?: ApolloClient<NormalizedCacheObject>,
): Promise<string | undefined> => {
  const horizonFeatures = ctx.req.horizonFeatures || []

  if (!horizonFeatures?.includes(Feature.Concessions)) {
    return undefined
  }

  const { sku } = getSafeUrlParametersFromReq<{ sku: string }>(ctx, 'sku')

  const skuNumber = parseInt(sku || '', 10)

  if (apolloClient) {
    const { data } = await apolloClient.query<{
      product: {
        concession: {
          concessionCode: string
        }
      }
    }>({
      query: PRODUCT_CONCESSION_QUERY,
      variables: {
        sku: skuNumber,
      },
    })

    if (data?.product?.concession?.concessionCode) {
      return data.product.concession.concessionCode
    }
    return undefined
  }

  return undefined
}

export const getServerSideProps = withCacheConfiguration<
  { props: ProductPageProps & { rayId: string | null } },
  GetServerSidePropsContext
>(async (context) => {
  const { sku, platformActionType, variation } = getSafeUrlParametersFromReq<{
    sku: string
    platformActionType: PlatformActionType
    variation: string
  }>(context, ['sku', 'platformActionType', 'variation'])

  const skuNumber = parseInt(sku || '', 10)

  const nextConfig = getConfig() as NextConfig
  const request = context.req as EnterpriseRequest
  const config = await loadConfiguration(nextConfig, request)

  const {
    publicRuntimeConfig,
    contentKeys = { productOptions: [], productDescriptionKeys: {} },
  } = config
  const {
    siteDefinition,
    siteConfig,
    shippingDestination: defaultShippingDestination,
  } = publicRuntimeConfig
  const {
    enableProductPersonalisation = false,
    enablePricePerUnit = false,
    useExternalIdentifier = false,
  } = siteConfig

  const { reviewFilters } = request.query
  const { allowOverrides, allowSitePropertyOverrides } = request.config
  const reviewScoreFilters = decodeURIComponent(reviewFilters?.toString() || '')

  const {
    productOptions: productContentKeys = [],
    productDescriptionKeys = {
      items: [],
      details: [],
      download: [],
      video: [],
    },
  } = contentKeys

  if (skuNumber) {
    const pageTheme = await loadPageTheme<ProductPageLayout>({
      page: 'product',
      brand: siteDefinition.brand,
      subsite: siteDefinition.subsite,
    })

    const graphql = createGraphQLClient(config, request)

    let data:
      | {
          productPage: ProductPageData & { rayId?: string | null }
          componentWidgets: ComponentWidgetsData['componentWidgets']
          rayId?: string | null
        }
      | undefined

    const queryStartTime = new Date()

    const horizonFeatures = request.horizonFeatures || []

    const enableHullabalookRecommendations =
      siteConfig.showHullabalookRecommendations ||
      request.headers['x-enterprise-hullabalook-recommendations'] === '1'

    const preselectVariant =
      (siteConfig.enablePreselectedProductVariant &&
        request.headers['x-enterprise-disable-preselected-variant'] !== '1') ??
      true

    const { data: auroraData } = await graphql.query<
      {
        productPage: ProductPageData & { rayId: string | null }
      },
      ProductPageVariables
    >(PRODUCT_PAGE_QUERY_AURORA, {
      preselectVariant,
      enablePricePerUnit,
      enableProductPersonalisation,
      useExternalIdentifier,
      productContentKeys,
      shippingDestination: getShippingDestination(
        request.config.sessionSettings?.shippingDestination,
        defaultShippingDestination.code as Country,
      ),
      currency: getCurrency(
        request.config.sessionSettings?.currency,
        siteDefinition.defaultCurrency,
      ),
      productCategoryKeys: siteConfig.alternateProductKeysForCategories,
      showPowerReview: siteConfig.showPowerReview,
      enableAlternateSwatch: siteConfig.enableAlternateSwatch || false,
      subscriptionContractsEnabled: horizonFeatures.includes(
        Feature.SubscribeAndSave,
      ),
      subscriptionsEnabled: horizonFeatures.includes(Feature.Subscriptions),
      descriptionKeys: productDescriptionKeys,
      tagKeys: siteConfig.productTagsKeys || [],
      sort: siteConfig.reviewSortMostToLeastPositiveVotes
        ? ReviewSort.MostToLeastPositiveVotes
        : ReviewSort.NewestToOldest,
      sku: skuNumber,
      buyNowPayLaterEnabled: siteConfig.buyNowPayLaterProvidersEnabled || false,
      loyaltyEnabled: horizonFeatures.includes(Feature.Loyalty),
      hasMultipleAvailabilitySources: horizonFeatures.includes(
        Feature.HasMultipleAvailabilitySources,
      ),
      notifyWhenInStockEnabled: horizonFeatures.includes(Feature.Waitlist),
      clickAndCollectEnabled:
        horizonFeatures.includes(Feature.ClickAndCollect) &&
        (siteConfig.enableClickAndCollect || false),
      manualRecommendationEnabled: horizonFeatures.includes(
        Feature.ManualRecommendations,
      ),
      manualRecommendationType: manualRecsType(
        request.query.manualRecommendationType,
        siteConfig.manualRecommendationType,
      ),
      enableHullabalookRecommendations:
        siteConfig.showHullabalookRecommendations,
      recommendationType: ProductRecommendationType.FrequentlyBoughtTogether,
      recommendationLimit: siteConfig.recommendationItemsLimit || 4,
      manualRecommendationLimit: 5,
      isProductContentKeysAvailable: isAlternateProductKeys(
        siteConfig.alternateProductKeysForCategories || [],
      ),
      reviewsSort: siteConfig.reviewInitialSort,
      reviewsFilter: {
        scores:
          getReviewFiltersFromUrl(reviewScoreFilters)?.scores?.map((item) =>
            parseInt(item, 10),
          ) || [],
      },
      enableHeroProductImageTag: siteConfig.enableHeroProductImageTag || false,
      vipPriceEnabled:
        siteConfig.enableVipPrice &&
        horizonFeatures.includes(Feature.VipPricingEnabled),
      preselectedVariantSku: parseInt(variation || '', 10),
      showUSPDeliveryInformation:
        siteConfig.showUSPDeliveryInformation || false,
    })

    const selectedOptions = getSelectedOptions(
      auroraData?.productPage?.sortedOptionKeys,
      auroraData?.productPage?.defaultSelectedChoices,
    )

    const { data: auroraComponentWidgets } = await graphql.query<any>(
      GLOBAL_COMPONENT_QUERY_AURORA,
    )

    if (selectedOptions) {
      auroraData.productPage.selectedOptions = selectedOptions
    }

    data = {
      productPage: auroraData.productPage,
      componentWidgets: auroraComponentWidgets.globalComponentWidgets,
      rayId: auroraData?.productPage?.rayId,
    }

    if (data?.productPage) {
      try {
        const histogram = getHistogram({
          name: 'enterprise_monitor_api_timings',
          labels: ['appname', 'brand', 'subsite', 'endpoint'],
          help: 'This metric stores the duration of API calls',
        })
        histogram
          .labels(
            'product',
            siteDefinition.brand,
            siteDefinition.subsite,
            'product_page_query',
          )
          .observe(new Date().getTime() - queryStartTime.getTime())
      } catch (error) {
        console.warn(`failed to raise metric for product page query: ${error}`)
      }

      const normalisedRequestUrl = request.browserUrl
        ?.split('?')[0]
        .replace(/^\/e2\//, '/')

      if (data.productPage.url !== normalisedRequestUrl) {
        const redirectURL = new URL(
          data.productPage.url,
          `https://${siteDefinition.domain}`,
        )
        Object.entries(request.query).forEach(([key, value]) => {
          if (typeof value === 'string') {
            const decodedValue = decodeURIComponent(value)
            redirectURL.searchParams.set(key, decodedValue)
          }
        })

        // Support proper UTF encoding for consistency
        redirectURL.search = redirectURL.searchParams
          .toString()
          .replace(/\+/g, '%20')

        context.res.setHeader('Location', redirectURL.toString())
        context.res.writeHead(301)
        context.res.end()

        // Return empty props to honour the types
        return {
          props: {
            componentWidgets: [],
            prefetchLinks: { defaults: [], variants: [] },
            pageLayout: null,
            manualRecommendations: null,
          },
        }
      }

      const { zoom, images } = data.productPage.gallery?.prefetches || {
        zoom: [],
        images: [],
      }
      const gridEnabled = pageTheme?.theme.imageGallery.grid?.enabled

      const defaultPrefetches = gridEnabled ? zoom.concat(images) : zoom

      const variantsPrefetchLinks = data.productPage.variants.reduce(
        (accumulator, variant) => {
          if (
            variant.sku === data?.productPage.defaultVariant.sku ||
            !variant.gallery ||
            !variant?.gallery?.prefetches
          ) {
            return accumulator
          }
          const { images, zoom } = variant?.gallery?.prefetches
          accumulator[variant.sku] = {
            defaults: gridEnabled ? zoom.concat(images) : zoom,
          }
          return accumulator
        },
        {},
      )

      const props: CacheConfigurationProps<ProductPageProps> = {
        enableHullabalookRecommendations,
        pageTheme,
        preselectVariant,
        enableAuroraProductOptions: true,
        rayId: data.rayId || null,
        prefetchLinks: {
          defaults: defaultPrefetches,
          variants: variantsPrefetchLinks,
        },
        variationSkuProduct: null,
        componentWidgets: data.componentWidgets || [],
        platformActionType: platformActionType ?? PlatformActionType.noAction,
        reviewsSort: siteConfig.reviewInitialSort,
        reviewFilters: reviewFilters?.toString() || '',
        product: data.productPage,
        manualRecommendations: data.productPage.manualRecommendations
          ? data.productPage.manualRecommendations.map((recommendation) => {
              const selectedOptions = getSelectedOptions(
                recommendation?.sortedOptionKeys,
                recommendation?.defaultSelectedChoices,
              )

              return {
                ...recommendation,
                selectedOptions,
              }
            })
          : null,
      }

      return {
        props,
        cache: {
          ...(pageTTLConfig(CachePageType.PRODUCT) || {
            ttl: 300,
            grace: 86400,
          }),
          tags: [
            generateObjectCacheKey(
              ObjectCacheKey.PRODUCT,
              data.productPage.sku,
            ),
          ],
          vary: data.productPage.varyHeaders,
        },
      }
    }

    const apollo = createApolloWithRequest(request, config, true)
    apollo.addResolvers(
      Resolvers(
        apollo,
        publicRuntimeConfig,
        allowOverrides,
        allowSitePropertyOverrides,
        request.config.enableVary,
      ),
    )

    const { data: notFoundErrorWidgets } = await apollo.query<
      {
        componentWidgets: Maybe<Widget[]>
      },
      QueryPageArgs & ComponentWidgetsVariables
    >({
      query: WIDGET_QUERY,
      variables: {
        shippingDestination: getShippingDestination(
          request.config.sessionSettings?.shippingDestination,
          defaultShippingDestination.code as Country,
        ),
        currency: getCurrency(
          request.config.sessionSettings?.currency,
          siteDefinition.defaultCurrency,
        ),
        path: Routes.Product,
        name: ComponentName.PAGE_UNAVAILABLE,
      },
    })

    return {
      props: {
        notFoundPageWidgets: notFoundErrorWidgets.componentWidgets || [],
        componentWidgets: [],
        prefetchLinks: { defaults: [], variants: [] },
        pageLayout: null,
        manualRecommendations: null,
      },
    }
  }
})

export default ProductPage
